import React, { useEffect, useContext, useRef, useState } from "react";
import { useSelector } from "react-redux";
import MapContext from "../MapContext";
import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import { getFeatureWithId } from "../utils/mapUtils";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import "./DataGrid.css";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { process } from "@progress/kendo-data-query";
import { Style, Stroke, Circle as CircleStyle } from "ol/style";
import { ImSpinner2 } from "react-icons/im";
import { inAndOut } from "ol/easing";
import GridHeader from "./GridHeader";
import {
    createGridData,
    createFieldsArray,
    createGeocodeFeatures,
    createGeocodeData,
    RowRender,
    CellRender,
} from "./gridUtils";

const initialDataState = {
    filter: undefined,
    group: undefined,
    skip: 0,
    sort: undefined,
    take: undefined,
};

const DataGrid = () => {
    const results = useSelector((state) => state.resultsData.results);
    const vectorLayers = useSelector((state) => state.configData.vectorLayerProperties);
    const geocodingData = useSelector((state) => state.resultsData.geocodingData);
    const authPermissions = useSelector((state) => state.authorizedPermissions);

    const { map } = useContext(MapContext);
    const [layerFields, setLayerFields] = useState(undefined);
    const [gridData, setGridData] = useState(undefined);
    const [selectedTab, setSelectedTab] = useState();
    const [selectedState, setSelectedState] = useState({});
    const [filterToggle, setFilterToggle] = useState(false);
    const [changes, setChanges] = useState(false);

    const [tempData, setTempData] = useState();

    const [dataState, setDataState] = useState(initialDataState);

    const [pageSizeValue, setPageSizeValue] = useState();

    const [collapsedState, setCollapsedState] = useState([]);

    const _export = useRef(null);
    const _grid = useRef(null);

    const vectorLayer = useRef();
    const vectorSelectionLayer = useRef();

    const EDIT_FIELD = "inEdit";

    const toggleFilter = () => {
        setFilterToggle(!filterToggle);
    };

    const filterChange = (e) => {
        // Return user to first page when filtering
        if (dataState.skip !== 0) {
            setDataState({ ...dataState, filter: e.filter, skip: 0 });
        } else {
            setDataState({ ...dataState, filter: e.filter });
        }
    };

    const expandChange = (e) => {
        console.log(e);
        const item = e.dataItem;
        if (item.groupId) {
            const newCollapsedIds = !e.value
                ? [...collapsedState, item.groupId]
                : collapsedState.filter((groupId) => groupId !== item.groupId);
            console.log(newCollapsedIds);
            setCollapsedState(newCollapsedIds);
        }
    };

    const excelExport = () => {
        if (_export.current !== null) {
            _export.current.save(gridData, _grid.current.columns);
        }
    };

    const pageChange = (e) => {
        const skip = e.page.skip;
        if (e.target.value) {
            setPageSizeValue(e.target.value);
        }
        setDataState({ ...dataState, skip: skip });
    };

    const handleTabSelect = (e) => {
        setGridData(undefined);
        setLayerFields(undefined);
        setSelectedTab(e.selected);
    };

    const stateDataChange = (e) => {
        setDataState(e.dataState);
    };

    const rowClick = (e) => {
        if (e.dataItem === null) return;
        // Zoom to layer
        const featureId = e.dataItem.featureId;
        const feature = getFeatureWithId(map, results[selectedTab].Id, featureId);
        const extent = feature.getGeometry().getExtent();
        map.getView().fit(extent, {
            padding: [50, 60, 60, 60],
            duration: 750,
            easing: inAndOut,
            minResolution: 1,
        });
    };

    const onHoverStart = (e) => {
        // Update selection state
        const iD = e.dataItem.featureId;
        const newBoolean = !selectedState[iD];
        const newObject = { [iD]: newBoolean };
        setSelectedState(newObject);
    };

    const onHoverEnd = (e) => {
        setSelectedState({});
    };

    const enterEdit = (dataItem, field) => {
        setTempData([...gridData]);
        const newData = gridData.map((item) => ({
            ...item,
            [EDIT_FIELD]: item.featureId === dataItem.featureId ? field : undefined,
        }));
        setGridData(newData);
    };

    const exitEdit = () => {
        const newData = gridData.map((item) => ({
            ...item,
            [EDIT_FIELD]: undefined,
        }));
        setGridData(newData);
    };

    const itemChange = (e) => {
        let field = e.field || "";
        e.dataItem[field] = e.value;
        let newData = gridData.map((item) => {
            if (item.featureId === e.dataItem.featureId) {
                item[field] = e.value;
            }
            return item;
        });
        setGridData(newData);
        setChanges(true);
    };

    const saveChanges = () => {
        setChanges(false);
        setTempData(undefined);
    };

    const cancelChanges = () => {
        setChanges(false);
        setGridData(tempData);
        setTempData(undefined);
    };

    // Add layers
    useEffect(() => {
        vectorLayer.current = new VectorLayer({
            source: new VectorSource(),
            map: map,
            style: selectedStyle,
            zIndex: 100,
        });
        vectorSelectionLayer.current = new VectorLayer({
            source: new VectorSource(),
            map: map,
            style: highlightStyle,
            zIndex: 101,
        });
        return () => {
            vectorLayer.current.getSource().clear();
            vectorSelectionLayer.current.getSource().clear();
        };
    }, []);

    // Update results layer
    useEffect(() => {
        if (gridData === undefined) return;
        setDataState(initialDataState);
        const featuresArray = [];
        if (results[selectedTab].DisplayName === "ArcGIS Geocoder") {
            featuresArray.push(
                ...createGeocodeFeatures(results[selectedTab], geocodingData)
            );
        } else {
            results[selectedTab].Features.forEach((featureId) => {
                const f = getFeatureWithId(map, results[selectedTab].Id, featureId);
                if (f != null) {
                    featuresArray.push(f);
                }
            });
        }
        vectorLayer.current.getSource().addFeatures(featuresArray);
        vectorLayer.current.changed();
        return () => {
            vectorLayer.current.getSource().clear();
        };
    }, [gridData]);

    // Update selected layer
    useEffect(() => {
        if (gridData === undefined) return;
        const selectedFeaturesArray = [];
        if (results[selectedTab].Id === "ArcGIS Geocoder") {
            const features = createGeocodeFeatures(results[selectedTab], geocodingData);
            features.forEach((f) => {
                const id = f.values_.Id;
                if (selectedState[id]) {
                    selectedFeaturesArray.push(f);
                }
            });
        } else {
            for (const s in selectedState) {
                if (selectedState[s]) {
                    const f = getFeatureWithId(map, results[selectedTab].Id, s);
                    if (f != null) {
                        selectedFeaturesArray.push(f);
                    }
                }
            }
        }

        vectorSelectionLayer.current.getSource().addFeatures(selectedFeaturesArray);
        return () => {
            vectorSelectionLayer.current.getSource().clear();
        };
    }, [selectedState]);

    // Calculate new results on tab change
    useEffect(() => {
        if (selectedTab === undefined) return;
        if (results[selectedTab] === undefined) {
            console.log("results undefined");
            return;
        }
        if (results[selectedTab].DisplayName === "ArcGIS Geocoder") {
            const [geoData, geoFields, geoSelected] = createGeocodeData(
                results[selectedTab],
                geocodingData
            );
            setSelectedState(geoSelected);
            setGridData(geoData);
            setLayerFields(geoFields);
        } else {
            const vectorFields = vectorLayers.find(
                (v) => v.Id === results[selectedTab].Id
            )?.Fields;
            const [newGridData, newSelectedState] = createGridData(
                map,
                results[selectedTab],
                vectorFields,
                true
            );
            // Get fields and check permissions
            const fields = createFieldsArray(
                vectorLayers,
                results[selectedTab].Id,
                authPermissions.fieldPermissions
            );
            setSelectedState(newSelectedState);
            setGridData(newGridData);
            setLayerFields(fields);
        }
    }, [selectedTab]);

    // Change tab or calculate new results on results change
    useEffect(() => {
        // Set grid data to trigger loading screen
        setGridData(undefined);
        setLayerFields(undefined);
        // If all results have been removed
        if (results.length === 0) {
            setSelectedTab(undefined); // This causes 'no data' to be displayed
        }
        // If right most tab is selected and results are moved
        else if (selectedTab > results.length - 1) {
            setSelectedTab(selectedTab - 1);
        }
        // First set of results, show the leftmost tab
        else if (results.length !== 0 && selectedTab === undefined) {
            setSelectedTab(0);
        }
        // No results have been remove and new results have been added
        else {
            if (results[selectedTab].DisplayName === "ArcGIS Geocoder") {
                const [geoData, geoFields, geoSelected] = createGeocodeData(
                    results[selectedTab],
                    geocodingData
                );
                setSelectedState(geoSelected);
                setGridData(geoData);
                setLayerFields(geoFields);
            } else {
                const vectorFields = vectorLayers.find(
                    (v) => v.Id === results[selectedTab].Id
                )?.Fields;
                const [newGridData, newSelectedState] = createGridData(
                    map,
                    results[selectedTab],
                    vectorFields,
                    true
                );
                // Get fields and check permissions
                const fields = createFieldsArray(
                    vectorLayers,
                    results[selectedTab].Id,
                    authPermissions.fieldPermissions
                );
                setSelectedState(newSelectedState);
                setGridData(newGridData);
                setLayerFields(fields);
            }
        }
    }, [results]);

    const customCellRender = (td, props) => (
        <CellRender
            originalProps={props}
            td={td}
            enterEdit={enterEdit}
            editField={EDIT_FIELD}
        />
    );

    const rowRender = (tr, props) => (
        <RowRender
            originalProps={props}
            tr={tr}
            onHoverStart={onHoverStart}
            onHoverEnd={onHoverEnd}
            exitEdit={exitEdit}
            editField={EDIT_FIELD}
        />
    );

    return (
        <>
            {results.length === 0 ? (
                <div>No Data</div>
            ) : (
                <>
                    <ExcelExport
                        ref={_export}
                        fileName={results[selectedTab]?.DisplayName ?? "Data"}
                    />
                    <div className="grid-view">
                        <GridHeader
                            toggleFilter={toggleFilter}
                            filterToggle={filterToggle}
                            excelExport={excelExport}
                            handleTabSelect={handleTabSelect}
                            selectedTab={selectedTab}
                            saveChanges={saveChanges}
                            cancelChanges={cancelChanges}
                            changes={changes}
                            length={gridData?.length ?? undefined}
                        />

                        {gridData !== undefined && layerFields !== undefined ? (
                            <div className="grid-wrapper">
                                <Grid
                                    ref={_grid}
                                    // add selected field here so it does not export to excel
                                    data={process(gridData, {
                                        skip: dataState.skip,
                                        take: dataState.take,
                                        sort: dataState.sort,
                                        filter: dataState.filter,
                                        group: dataState.group,
                                    })}
                                    rowRender={rowRender}
                                    // Grouping
                                    // groupable={true}
                                    group={dataState.group}
                                    onExpandChange={expandChange}
                                    // onGroupChange={groupChange}
                                    expandField={"expanded"}
                                    // Scroll
                                    scrollable={"virtual"}
                                    // Columns
                                    resizable={true}
                                    reorderable={true}
                                    // Pagination
                                    skip={dataState.skip}
                                    take={dataState.take}
                                    total={gridData.length}
                                    pageSize={50}
                                    rowHeight={40}
                                    onPageChange={pageChange}
                                    //Edit
                                    editField="inEdit"
                                    cellRender={customCellRender}
                                    dataItemKey={"featureId"}
                                    onItemChange={itemChange}
                                    // Filter
                                    filter={dataState.filter}
                                    filterable={filterToggle}
                                    onFilterChange={filterChange}
                                    // Sorting
                                    onDataStateChange={stateDataChange}
                                    sortable={true}
                                    sort={dataState.sort}
                                    // Zoom to features
                                    onRowClick={rowClick}
                                    //
                                    style={{
                                        position: "absolute",
                                        height: "100%",
                                        width: "100%",
                                        paddingBottom: "8px",
                                    }}>
                                    {layerFields.map((l) => {
                                        const title = l.DisplayName;
                                        const field = l.Name;
                                        return (
                                            <GridColumn
                                                field={field}
                                                title={title}
                                                resizable={true}
                                                editor="text"
                                                width="120px"
                                                key={l.DisplayName}
                                                // Update below when editing is set up for grid view
                                                editable={false}
                                                // editable={l.Editable}
                                            />
                                        );
                                    })}
                                    <GridColumn editable={false} filterable={false} />
                                </Grid>
                            </div>
                        ) : (
                            <ImSpinner2 className="grid-spinner" />
                        )}
                    </div>
                </>
            )}
        </>
    );
};

export default DataGrid;

const selectedStyle = new Style({
    zIndex: 100,
    stroke: new Stroke({
        color: "#FF0000",
        width: 3,
    }),
    image: new CircleStyle({
        radius: 10,
        stroke: new Stroke({
            color: "#FF0000",
            width: 4,
        }),
    }),
});

const highlightStyle = new Style({
    zIndex: 100,
    stroke: new Stroke({
        color: "#00FFFF",
        width: 3,
    }),
    image: new CircleStyle({
        radius: 10,
        stroke: new Stroke({
            color: "#00FFFF",
            width: 4,
        }),
    }),
});
